import axios from 'axios'
import type { Customer, FeedbackData, ComplaintData } from '@/stores/feedback'

// Use Vercel API for production (Firebase Functions requires Blaze plan)
const finalBaseURL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:3001/api' 
  : `${window.location.origin}/api`

console.log('🚀 Current hostname:', window.location.hostname)
console.log('🌐 Using API URL:', finalBaseURL)

const api = axios.create({
  baseURL: finalBaseURL,
  timeout: 20000, // Increased from 10000 to 20000 (20 seconds)
})

export interface SentimentAnalysisResponse {
  sentiment: 'positive' | 'negative' | 'neutral' | 'inquiry'
  category?: 'POSITIVE' | 'NEGATIVE' | 'INQUIRY'
  confidence: number
  response: string
  keywords: string[]
  reasoning?: string
  fallback?: boolean
}

export interface SubmitFeedbackResponse {
  success: boolean
  message: string
  caseNumber?: string
}

// Customer token validation
export async function validateCustomerToken(token: string): Promise<Customer | null> {
  try {
    const response = await api.post('/feedback/validate-token', { token })
    return response.data.valid ? response.data.customer : null
  } catch (error) {
    console.error('Token validation failed:', error)
    return null
  }
}

// AI Sentiment Analysis using Gemini
export async function analyzeSentiment(text: string, rating?: number): Promise<SentimentAnalysisResponse> {
  console.log('🔍 Analyzing sentiment:', { text, rating })
  
  try {
    // Pass both text and rating to the backend API
    const response = await api.post('/feedback/analyze-sentiment', { text, rating })
    return response.data
  } catch (error) {
    console.error('❌ Sentiment analysis failed:', error)
    
    // Fallback analysis based on rating if API fails
    let fallbackSentiment: 'positive' | 'negative' | 'neutral' | 'inquiry' = 'neutral'
    if (rating) {
      if (rating <= 2) fallbackSentiment = 'negative'
      else if (rating === 3) fallbackSentiment = 'neutral'
      else fallbackSentiment = 'positive'
    } else {
      // No rating provided, could be inquiry
      const inquiryKeywords = ['when', 'what time', 'how much', 'price', 'cost', 'hours', 'open', 'closed']
      const isInquiry = inquiryKeywords.some(keyword => text.toLowerCase().includes(keyword))
      if (isInquiry) fallbackSentiment = 'inquiry'
    }
    
    console.log('🔄 Using fallback sentiment:', fallbackSentiment)
    
    return {
      sentiment: fallbackSentiment,
      confidence: 0.6,
      response: 'Thank you for your feedback. We appreciate you taking the time to share your experience.',
      keywords: [],
      fallback: true
    }
  }
}

// Submit positive feedback
export async function submitPositiveFeedback(
  customer: Customer,
  feedback: FeedbackData
): Promise<SubmitFeedbackResponse> {
  try {
    const response = await api.post('/submitPositive', {
      customer,
      feedback
    })
    return response.data
  } catch (error) {
    console.error('Failed to submit positive feedback:', error)
    throw new Error('Failed to submit feedback')
  }
}

// Submit complaint
export async function submitComplaint(
  customer: Customer,
  feedback: FeedbackData,
  complaint: ComplaintData
): Promise<SubmitFeedbackResponse> {
  try {
    const response = await api.post('/submitComplaint', {
      customer,
      feedback,
      complaint
    })
    return response.data
  } catch (error) {
    console.error('Failed to submit complaint:', error)
    throw new Error('Failed to submit complaint')
  }
}

// Submit inquiry
export async function submitInquiry(
  customer: Customer,
  feedback: FeedbackData,
  inquiry: any
): Promise<SubmitFeedbackResponse> {
  try {
    const response = await api.post('/submitInquiry', {
      customer,
      feedback,
      inquiry
    })
    return response.data
  } catch (error) {
    console.error('Failed to submit inquiry:', error)
    throw new Error('Failed to submit inquiry')
  }
}

// Get customer info by name (for simple token system)
export async function getCustomerByName(name: string): Promise<Customer | null> {
  try {
    const response = await api.get(`/getCustomer/${encodeURIComponent(name)}`)
    return response.data.customer || null
  } catch (error) {
    console.error('Failed to get customer info:', error)
    return null
  }
}

// Track review button clicks
export async function trackReviewClick(data: {
  platform: 'google' | 'yelp'
  caseNumber?: string
  customerName: string
  timestamp: string
  feedbackRating?: number
  feedbackText: string
}): Promise<void> {
  try {
    await api.post('/feedback/track-review-click', data)
    console.log('✅ Review click tracked:', data.platform)
  } catch (error) {
    console.error('❌ Failed to track review click:', error)
    throw error
  }
}

// Submit phone number for coupon delivery
export async function submitPhoneNumber(data: {
  caseNumber: string
  phoneNumber: string
  customerName?: string
  customer?: Customer
  feedback?: FeedbackData
  reviewClicked?: boolean
  reviewPlatform?: string | null
}) {
  const response = await fetch(`${finalBaseURL}/feedback/phone`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  return response.json()
}

// Export all functions as apiService object for easier importing
export const apiService = {
  validateCustomerToken,
  analyzeSentiment,
  submitPositiveFeedback,
  submitComplaint,
  submitInquiry,
  getCustomerByName,
  trackReviewClick,
  submitPhoneNumber
} 